import React from 'react';
import {
  Box,
  Input,
  InputGroup,
  InputLeftElement,
  HStack,
  VStack,
  Badge,
  Button,
  Text,
  Spinner,
  SimpleGrid,
  FormLabel,
  Divider
} from '@chakra-ui/react';
import { SearchIcon, CloseIcon } from '@chakra-ui/icons';

interface FilterField {
  key: string;
  type: string;
  placeholder?: string;
  options?: Array<{ value: any; label: string }>;
  icon?: React.ReactNode;
}

interface SearchFiltersWithProgressiveSearchProps {
  // Búsqueda progresiva
  searchTerm: string;
  isSearching: boolean;
  onSearchChange: (term: string) => void;
  onClearSearch: () => void;
  
  // Filtros locales
  filters: Record<string, any>;
  filterFields: FilterField[];
  hasLocalFilters: boolean;
  onFilterChange: (key: string, value: any) => void;
  onClearLocalFilters: () => void;
  
  // Estados generales
  totalResults: number;
  hasActiveFilters: boolean;
  title?: string;
}

export const SearchFiltersWithProgressiveSearch: React.FC<SearchFiltersWithProgressiveSearchProps> = ({
  searchTerm,
  isSearching,
  onSearchChange,
  onClearSearch,
  filters,
  filterFields,
  hasLocalFilters,
  onFilterChange,
  onClearLocalFilters,
  totalResults,
  hasActiveFilters,
  title = "Búsqueda y Filtros"
}) => {
  const clearAll = () => {
    onClearSearch();
    onClearLocalFilters();
  };

  return (
    <Box mb={6} p={4} bg="white" borderRadius="lg" shadow="sm" border="1px" borderColor="gray.200">
      <VStack spacing={4} align="stretch">
        <HStack justify="space-between" align="center">
          <Text fontSize="lg" fontWeight="semibold" color="gray.700">
            {title}
          </Text>
          {hasActiveFilters && (
            <Button
              size="sm"
              variant="ghost"
              colorScheme="red"
              leftIcon={<CloseIcon />}
              onClick={clearAll}
            >
              Limpiar todo
            </Button>
          )}
        </HStack>

        {/* Búsqueda progresiva */}
        <VStack spacing={3} align="stretch">
          <Text fontSize="md" fontWeight="medium" color="gray.600">
            Búsqueda Global
          </Text>
          
          <HStack spacing={4} align="center">
            <InputGroup flex={1} maxW="500px">
              <InputLeftElement>
                {isSearching ? (
                  <Spinner size="sm" color="blue.500" />
                ) : (
                  <SearchIcon color="gray.400" />
                )}
              </InputLeftElement>
              <Input
                placeholder="Buscar en todos los predios..."
                value={searchTerm}
                onChange={(e) => onSearchChange(e.target.value)}
                bg="gray.50"
                border="1px"
                borderColor="gray.300"
                _focus={{
                  bg: "white",
                  borderColor: "blue.500",
                  boxShadow: "0 0 0 1px #3182ce"
                }}
              />
            </InputGroup>

            {searchTerm && (
              <Button size="sm" variant="ghost" onClick={onClearSearch}>
                Limpiar búsqueda
              </Button>
            )}

            {searchTerm && (
              <Badge
                colorScheme={totalResults > 0 ? "green" : "gray"}
                variant="subtle"
                px={3}
                py={1}
                borderRadius="full"
              >
                {totalResults.toLocaleString()} resultado{totalResults !== 1 ? 's' : ''}
              </Badge>
            )}
          </HStack>

          {isSearching && (
            <Text fontSize="sm" color="blue.600">
              Buscando en todos los predios...
            </Text>
          )}
        </VStack>

        {/* Separador */}
        {!searchTerm && (
          <>
            <Divider />
            
            {/* Filtros locales */}
            <VStack spacing={3} align="stretch">
              <HStack justify="space-between" align="center">
                <Text fontSize="md" fontWeight="medium" color="gray.600">
                  Filtros Locales
                </Text>
                {hasLocalFilters && (
                  <Button size="sm" variant="ghost" onClick={onClearLocalFilters}>
                    Limpiar filtros
                  </Button>
                )}
              </HStack>

              <SimpleGrid columns={{ base: 1, md: 2, lg: 3, xl: 4 }} spacing={4}>
                {filterFields.map((field) => (
                  <Box key={field.key}>
                    {field.type === 'select' && field.options ? (
                      <select
                        value={filters[field.key] || ''}
                        onChange={(e) => onFilterChange(field.key, e.target.value)}
                        style={{
                          width: '100%',
                          padding: '8px',
                          borderRadius: '6px',
                          border: '1px solid #e2e8f0',
                          backgroundColor: '#f7fafc',
                          fontSize: '14px'
                        }}
                      >
                        <option value="">{field.placeholder || 'Seleccionar'}</option>
                        {field.options.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <Input
                        placeholder={field.placeholder || 'Filtrar'}
                        value={filters[field.key] || ''}
                        onChange={(e) => onFilterChange(field.key, e.target.value)}
                        size="sm"
                        bg="gray.50"
                      />
                    )}
                  </Box>
                ))}
              </SimpleGrid>
            </VStack>
          </>
        )}
      </VStack>
    </Box>
  );
};